import { Component } from "react"
import PropTypes from "prop-types"
import { get } from "lodash"
import { PERIOD, CONTENT_SET } from "helpers/shapes"
import { disableSearchResult, getBackwardsCompatibleSelectedType } from "helpers/comparison"
import SaveExperiment from "modules/save-experiment"
import CompareSettings from "modules/comparison-settings"
import ComparePerformance from "modules/compare-performance"
import CompareRisk from "modules/compare-risk"
import CompareExposure from "modules/compare-exposure"
import CompareComposition from "modules/compare-composition"
import CompareFrontier from "modules/compare-frontier"
import CompareSegments from "modules/compare-segments-root"
import ExperimentTrades from "modules/experiment-trades"
import AddOptions from "./components/add-options"
import EditPortfolio from "modules/edit-portfolio"
import { isMetricFactorSensitivity, getFactorSensitivityTypeFromMetric } from "helpers/factorSensitivity"
import CompareFactorSensitivity from "modules/compare-factor-sensitivity"
import { VIEW_TYPES } from "helpers/base"

import "./style.scss"

export default class Experiments extends Component {
  static propTypes = {
    baseUrl: PropTypes.string.isRequired,
    comparisonItems: PropTypes.array.isRequired,
    earliestDate: PropTypes.string.isRequired,
    latestDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    startDate: PropTypes.string.isRequired,
    selectedDate: PropTypes.string.isRequired,
    frontierDateAdjusted: PropTypes.bool.isRequired,
    setSelectedDate: PropTypes.func.isRequired,
    period: PERIOD.isRequired,
    setPeriod: PropTypes.func.isRequired,
    setSelectedComparisonType: PropTypes.func.isRequired,
    resetSelectedDate: PropTypes.func.isRequired,
    selectedComparisonType: PropTypes.object.isRequired,
    account: PropTypes.string.isRequired,
    modelId: PropTypes.string.isRequired,
    viewType: PropTypes.string.isRequired,
    base: PropTypes.object.isRequired,
    saveMetricToStore: PropTypes.func.isRequired,
    savedMetrics: PropTypes.object.isRequired,
    saveCompositionGroupToStore: PropTypes.func.isRequired,
    savedCompositionGroup: PropTypes.string.isRequired,
    nonOverlappingItems: PropTypes.array.isRequired,
    removeNonOverlappingItems: PropTypes.func.isRequired,
    openDialog: PropTypes.func.isRequired,
    searchItemsActions: PropTypes.object.isRequired,
    portfolio: PropTypes.string.isRequired,
    trackPanelTrigger: PropTypes.func.isRequired,
    trackExperimentTypeSelection: PropTypes.func.isRequired,
    toggleTradesPanel: PropTypes.func.isRequired,
    setFrontierDate: PropTypes.func.isRequired,
    frontierDate: PropTypes.string,
    tradesPanelOpen: PropTypes.bool.isRequired,
    selectedExperiment: PropTypes.object,
    loadEditExperimentIndex: PropTypes.func.isRequired,
    selectedContentSetId: PropTypes.string,
    setComparisonItems: PropTypes.func.isRequired,
    toggleRenameExperiment: PropTypes.func.isRequired,
    experimentToUpdate: PropTypes.object,
    trackSetPeriod: PropTypes.func.isRequired,
    contentSets: PropTypes.arrayOf(CONTENT_SET).isRequired,
    comparisonTypes: PropTypes.array.isRequired,
    models: PropTypes.object.isRequired,
    newPortfolio: PropTypes.object,
    convertExperiment: PropTypes.func.isRequired,
    toggleCreatePortfolio: PropTypes.func.isRequired,
    experimentToConvert: PropTypes.object,
    isOpPro: PropTypes.bool.isRequired,
    intradayAccountEnabled: PropTypes.bool,
    portfolioCurrency: PropTypes.string,
    portfolioUUID: PropTypes.string.isRequired,
  }

  componentDidMount() {
    const { nonOverlappingItems, removeNonOverlappingItems } = this.props
    if (nonOverlappingItems.length > 0) {
      removeNonOverlappingItems(nonOverlappingItems)
    }
  }

  componentDidUpdate(prevProps) {
    const { nonOverlappingItems, removeNonOverlappingItems, toggleRenameExperiment, experimentToUpdate, experimentToConvert, selectedExperiment } = this.props

    if (toggleRenameExperiment && experimentToUpdate && prevProps.experimentToUpdate !== experimentToUpdate) {
      this.renameExperiment(experimentToUpdate)
    }

    if (this.toggleTradesPanel && selectedExperiment && prevProps.selectedExperiment !== selectedExperiment) {
      this.toggleTradesPanel(selectedExperiment)
    }

    if (experimentToConvert && prevProps.experimentToConvert !== experimentToConvert) {
      this.openConvertExperiment()
    }

    if (nonOverlappingItems.length > 0) {
      removeNonOverlappingItems(nonOverlappingItems)
    }
  }

  onClose = () => {
    const { toggleTradesPanel } = this.props
    toggleTradesPanel(null)
  }

  toggleSaveExperiment = () => {
    const { openDialog, selectedExperiment, modelId, portfolioUUID, portfolio } = this.props
    let experimentToUpdate, label, title
    if (selectedExperiment) {
      title = "Save Experiment"
      label = "Save Experiment"
      experimentToUpdate = { ...selectedExperiment, name: selectedExperiment.label, type: selectedExperiment.experimentType, modelId }
    } else {
      title = "Create Experiment"
      label = "Upload Experiment"
      experimentToUpdate = { type: "PORTFOLIO" }
    }
    openDialog({
      title,
      content: <SaveExperiment experiment={experimentToUpdate} submitLabel={label} portfolioAlias={portfolio} portfolioId={portfolioUUID} />,
    })
  }

  renameExperiment = (experiment) => {
    const { openDialog } = this.props
    openDialog({
      title: `Update Experiment : ${experiment.name} `,
      content: <SaveExperiment isEditMode experiment={experiment} submitLabel="Update Experiment" />,
    })
  }

  createPortfolioAndView = (portfolio, showPortfolio) => {
    const { convertExperiment, experimentToConvert } = this.props
    convertExperiment(experimentToConvert, portfolio, showPortfolio)
  }

  openConvertExperiment = () => {
    const { modelId, openDialog } = this.props
    openDialog({
      title: "Create Portfolio From Experiment",
      content: <EditPortfolio
        defaultModelId={modelId}
        savePortfolio={this.createPortfolioAndView}
        allowShowPortfolio
      />,
    })
  }

  render() {
    const {
      baseUrl,
      earliestDate,
      latestDate,
      endDate,
      startDate,
      period,
      setPeriod,
      setSelectedComparisonType,
      selectedComparisonType,
      account,
      modelId,
      selectedDate,
      resetSelectedDate,
      comparisonItems,
      setSelectedDate,
      base,
      viewType,
      saveMetricToStore,
      saveCompositionGroupToStore,
      savedCompositionGroup,
      savedMetrics,
      openDialog,
      searchItemsActions,
      portfolio,
      trackExperimentTypeSelection,
      trackPanelTrigger,
      tradesPanelOpen,
      setFrontierDate,
      selectedExperiment,
      frontierDate,
      frontierDateAdjusted,
      setComparisonItems,
      trackSetPeriod,
      contentSets,
      comparisonTypes,
      isOpPro,
      portfolioCurrency,
    } = this.props

    const addOptions = <AddOptions
      openDialog={openDialog}
      account={account}
      portfolio={portfolio}
      modelId={modelId}
      searchItemsActions={searchItemsActions}
      disableSearchResult={disableSearchResult}
      toggleSaveExperiment={this.toggleSaveExperiment}
      trackExperimentTypeSelection={trackExperimentTypeSelection}
      isOpPro={isOpPro}
    />

    const { id: selectedId, type } = getBackwardsCompatibleSelectedType(selectedComparisonType)
    const isContentSet = type === "contentset"
    const saveExposureMetric = isContentSet ? selectedId : "exposure"

    return <div className="modules_experiments">
      <CompareSegments
        account={account}
        modelId={modelId}
        comparisonItems={comparisonItems}
        endDate={endDate}
        startDate={startDate}
        setParentComparisonItems={(items) => setComparisonItems(items)}
        currency={portfolioCurrency}
      >
        <CompareSettings
          account={account}
          modelId={modelId}
          comparisonItems={comparisonItems}
          earliestDate={earliestDate}
          latestDate={latestDate}
          endDate={endDate}
          startDate={startDate}
          period={period}
          setPeriod={setPeriod}
          setSelectedComparisonType={setSelectedComparisonType}
          selectedComparisonType={selectedComparisonType}
          selectedDate={selectedDate}
          resetDate={resetSelectedDate}
          addOptions={addOptions}
          frontierDate={frontierDate}
          setFrontierDate={setFrontierDate}
          frontierDateAdjusted={frontierDateAdjusted}
          trackSetPeriod={trackSetPeriod}
          contentSets={contentSets}
          baseUrl={baseUrl}
          comparisonTypes={comparisonTypes}
          isActive={viewType === VIEW_TYPES.ACTIVE}
        />
      </CompareSegments>
      {selectedId === "performance" && <ComparePerformance
        account={account}
        modelId={modelId}
        positionSets={comparisonItems}
        base={base}
        startDate={startDate}
        endDate={endDate}
        selectedDate={selectedDate}
        setDate={setSelectedDate}
        viewType={viewType}
        saveMetricToStore={saveMetricToStore}
        savedMetric={savedMetrics ? savedMetrics.performance : null}
        trackPanelTrigger={trackPanelTrigger}
        earliestDate={earliestDate}
        currency={portfolioCurrency}
      />}
      {selectedId === "risk" && <CompareRisk
        account={account}
        modelId={modelId}
        positionSets={comparisonItems}
        base={base}
        startDate={startDate}
        endDate={endDate}
        selectedDate={selectedDate}
        setDate={setSelectedDate}
        viewType={viewType}
        saveMetricToStore={saveMetricToStore}
        savedMetric={savedMetrics ? savedMetrics.risk : null}
        trackPanelTrigger={trackPanelTrigger}
        currency={portfolioCurrency}
      />}
      {(selectedId === "exposure" || isContentSet) && <CompareExposure
        account={account}
        modelId={modelId}
        positionSets={comparisonItems}
        base={base}
        startDate={startDate}
        endDate={endDate}
        selectedDate={selectedDate}
        setDate={setSelectedDate}
        viewType={viewType}
        saveMetricToStore={saveMetricToStore}
        savedMetric={savedMetrics ? savedMetrics[saveExposureMetric] : null}
        trackPanelTrigger={trackPanelTrigger}
        contentSetId={isContentSet ? selectedId : null}
      />}
      {selectedId === "composition" && <CompareComposition
        account={account}
        modelId={modelId}
        positionSets={comparisonItems}
        base={base}
        startDate={startDate}
        endDate={endDate}
        selectedDate={selectedDate}
        setDate={setSelectedDate}
        viewType={viewType}
        saveMetricToStore={saveMetricToStore}
        saveGroupToStore={saveCompositionGroupToStore}
        savedMetric={savedMetrics ? savedMetrics.composition : null}
        savedGroup={savedCompositionGroup}
        currency={portfolioCurrency}
      />}
      {selectedId === "frontier" && <CompareFrontier
        account={account}
        modelId={modelId}
        positionSets={comparisonItems}
        base={base}
        startDate={startDate}
        endDate={endDate}
        frontierDate={frontierDate}
        setDate={setSelectedDate}
        viewType={viewType}
        saveGroupToStore={saveCompositionGroupToStore}
        savedGroup={savedCompositionGroup}
        trackPanelTrigger={trackPanelTrigger}
        currency={portfolioCurrency}
        isOpPro={isOpPro}
      />}
      {isMetricFactorSensitivity(selectedId) && <CompareFactorSensitivity
        factorSensitivityType={getFactorSensitivityTypeFromMetric(selectedId)}
        account={account}
        modelId={modelId}
        positionSets={comparisonItems}
        base={base}
        startDate={startDate}
        endDate={endDate}
        selectedDate={selectedDate}
        setDate={setSelectedDate}
        viewType={viewType}
        saveMetricToStore={saveMetricToStore}
        savedMetric={savedMetrics ? get(savedMetrics, selectedId) : null}
        trackPanelTrigger={trackPanelTrigger}
        currency={portfolioCurrency}
      />}
      {selectedExperiment && <ExperimentTrades
        noSpinner
        on={selectedDate}
        open={tradesPanelOpen}
        onClose={this.onClose}
        experiment={selectedExperiment}
        base={base}
        selectedDate={selectedDate}
        toggleSaveExperiment={this.toggleSaveExperiment}
      />}
    </div>
  }
}
